// src/dnd/views/Sidebar_parts/MapPresenceAssignmentsPanel.js
import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = window.location.origin;

function toInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : null;
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function viewerLabel(viewer) {
  return (
    String(
      viewer?.display_name ||
        viewer?.username ||
        viewer?.name ||
        viewer?.controlled_username ||
        `User ${viewer?.id ?? viewer?.user_id ?? "?"}`
    ).trim() || "Unknown user"
  );
}

function dedupePresenceUsers(users) {
  const map = new Map();

  safeArr(users).forEach((u) => {
    if (!u) return;
    const userId = toInt(u?.id ?? u?.user_id);
    if (!userId || userId <= 0) return;

    const existing = map.get(userId);
    const currentLastSeen = Number(u?.last_seen) || 0;
    const existingLastSeen = Number(existing?.last_seen) || 0;

    if (!existing || currentLastSeen >= existingLastSeen) {
      map.set(userId, {
        ...u,
        id: userId,
        user_id: userId,
        label: viewerLabel(u),
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

function normalizeTokens(rows) {
  return safeArr(rows)
    .filter((row) => row && row.id != null)
    .map((row) => ({
      ...row,
      id: toInt(row.id),
      controlled_user_id:
        row?.controlled_user_id == null || row?.controlled_user_id === ""
          ? null
          : toInt(row.controlled_user_id),
      label:
        String(row?.label || `Token ${row?.id ?? "?"}`).trim() ||
        `Token ${row?.id ?? "?"}`,
      type: String(row?.type || "").trim().toLowerCase(),
    }))
    .filter((row) => row.id && row.type === "player")
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

export default function MapPresenceAssignmentsPanel({
  mapId,
  mapPresenceUsers = [],
  onlineMapUserIds = new Set(),
  mapPresenceUpdatedAt = 0,
  styles = {},
  fontColour = "#fff",
  mainFont = "inherit",
  isMobile = false,
}) {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [busyTokenId, setBusyTokenId] = useState(null);
  const [status, setStatus] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("mapPresenceAssignmentsCollapsed");
      return v ? v === "1" : false;
    } catch {
      return false;
    }
  });
  const [tokensCollapsed, setTokensCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("mapPresencePlayerTokensCollapsed");
      return v ? v === "1" : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("mapPresenceAssignmentsCollapsed", collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem("mapPresencePlayerTokensCollapsed", tokensCollapsed ? "1" : "0");
    } catch {}
  }, [tokensCollapsed]);

  const connectedUsers = useMemo(() => {
    const deduped = dedupePresenceUsers(mapPresenceUsers);
    return deduped.filter((u) => {
      const userId = toInt(u?.id ?? u?.user_id);
      if (!userId) return false;
      if (!(onlineMapUserIds instanceof Set)) return true;
      return onlineMapUserIds.has(userId);
    });
  }, [mapPresenceUsers, onlineMapUserIds]);

  const selectedUser = useMemo(() => {
    return connectedUsers.find((u) => u.user_id === selectedUserId) || null;
  }, [connectedUsers, selectedUserId]);

  const selectedToken = useMemo(() => {
    const tokenId = toInt(selectedTokenId);
    if (!tokenId) return null;
    return tokens.find((t) => t.id === tokenId) || null;
  }, [tokens, selectedTokenId]);

  const assignmentMap = useMemo(() => {
    const map = new Map();
    tokens.forEach((token) => {
      const controlledUserId = toInt(token.controlled_user_id);
      if (controlledUserId && controlledUserId > 0) {
        map.set(controlledUserId, token);
      }
    });
    return map;
  }, [tokens]);

  const userLabelMap = useMemo(() => {
    const map = new Map();
    connectedUsers.forEach((user) => {
      const userId = toInt(user?.user_id ?? user?.id);
      if (!userId) return;
      map.set(userId, user.label || viewerLabel(user));
    });
    return map;
  }, [connectedUsers]);

  useEffect(() => {
    if (!selectedUserId && connectedUsers.length) {
      setSelectedUserId(connectedUsers[0].user_id);
      return;
    }

    if (selectedUserId && !connectedUsers.some((u) => u.user_id === selectedUserId)) {
      setSelectedUserId(connectedUsers[0]?.user_id || null);
    }
  }, [connectedUsers, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedTokenId("");
      return;
    }

    const assigned = assignmentMap.get(selectedUserId);
    setSelectedTokenId(assigned?.id ? String(assigned.id) : "");
  }, [selectedUserId, assignmentMap]);

  useEffect(() => {
    if (!mapId) {
      setTokens([]);
      return;
    }

    let alive = true;
    let timer = null;

    const loadTokens = async () => {
      setLoadingTokens(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/tabletop/tokens/${mapId}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to load tokens");
        const data = await res.json();
        if (!alive) return;
        setTokens(normalizeTokens(data));
      } catch {
        if (!alive) return;
        setTokens([]);
      } finally {
        if (alive) setLoadingTokens(false);
      }
    };

    loadTokens();
    timer = setInterval(loadTokens, 6000);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [mapId, mapPresenceUpdatedAt]);

  const saveAssignment = async () => {
    if (!selectedUser || !selectedToken) return;

    setBusyTokenId(selectedToken.id);
    setStatus("");

    try {
      const payload = {
        label: selectedToken.label,
        icon_url: selectedToken.icon_url,
        type: selectedToken.type,
        x: Number(selectedToken.x),
        y: Number(selectedToken.y),
        controlled_user_id: selectedUser.user_id,
        intensity: selectedToken.intensity ?? 1.0,
        reach: selectedToken.reach ?? 350,
        colour: selectedToken.colour ?? "#ffe072",
        light_type: selectedToken.light_type ?? "none",
        emits_light: selectedToken.emits_light ?? 0,
        flicker: selectedToken.flicker ?? 0,
        visible_to_all: selectedToken.visible_to_all ?? 1,
        azimuth_deg: selectedToken.azimuth_deg ?? null,
        elevation_deg: selectedToken.elevation_deg ?? null,
        softness: selectedToken.softness ?? null,
        preset: selectedToken.preset ?? null,
        enabled: selectedToken.enabled ?? null,
      };

      const res = await fetch(`${API_BASE_URL}/api/tabletop/tokens/${selectedToken.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to assign token");
      }

      const updatedToken = {
        ...selectedToken,
        ...data,
        id: toInt(data?.id ?? selectedToken.id),
        controlled_user_id: toInt(data?.controlled_user_id ?? selectedUser.user_id),
        label: String(data?.label || selectedToken.label || "").trim() || selectedToken.label,
        type: String(data?.type || selectedToken.type || "").trim().toLowerCase(),
      };

      setTokens((prev) => {
        const next = prev.map((token) => {
          if (token.id === updatedToken.id) return updatedToken;
          if (toInt(token.controlled_user_id) === selectedUser.user_id) {
            return {
              ...token,
              controlled_user_id: null,
              controlled_username: null,
            };
          }
          return token;
        });
        return next;
      });

      setStatus(`${selectedUser.label} is now linked to ${updatedToken.label}.`);
    } catch (err) {
      setStatus(String(err?.message || "Failed to assign token"));
    } finally {
      setBusyTokenId(null);
    }
  };

  const clearAssignment = async () => {
    if (!selectedUser) return;

    const assignedToken = assignmentMap.get(selectedUser.user_id);
    if (!assignedToken) return;

    setBusyTokenId(assignedToken.id);
    setStatus("");

    try {
      const payload = {
        label: assignedToken.label,
        icon_url: assignedToken.icon_url,
        type: assignedToken.type,
        x: Number(assignedToken.x),
        y: Number(assignedToken.y),
        controlled_user_id: null,
        intensity: assignedToken.intensity ?? 1.0,
        reach: assignedToken.reach ?? 350,
        colour: assignedToken.colour ?? "#ffe072",
        light_type: assignedToken.light_type ?? "none",
        emits_light: assignedToken.emits_light ?? 0,
        flicker: assignedToken.flicker ?? 0,
        visible_to_all: assignedToken.visible_to_all ?? 1,
        azimuth_deg: assignedToken.azimuth_deg ?? null,
        elevation_deg: assignedToken.elevation_deg ?? null,
        softness: assignedToken.softness ?? null,
        preset: assignedToken.preset ?? null,
        enabled: assignedToken.enabled ?? null,
      };

      const res = await fetch(`${API_BASE_URL}/api/tabletop/tokens/${assignedToken.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to clear token assignment");
      }

      const updatedToken = {
        ...assignedToken,
        ...data,
        id: toInt(data?.id ?? assignedToken.id),
        controlled_user_id: null,
        controlled_username: null,
        label: String(data?.label || assignedToken.label || "").trim() || assignedToken.label,
        type: String(data?.type || assignedToken.type || "").trim().toLowerCase(),
      };

      setTokens((prev) => {
        return prev.map((token) => {
          if (token.id === updatedToken.id) return updatedToken;
          return token;
        });
      });

      setSelectedTokenId("");
      setStatus(`Removed token link for ${selectedUser.label}.`);
    } catch (err) {
      setStatus(String(err?.message || "Failed to clear token assignment"));
    } finally {
      setBusyTokenId(null);
    }
  };

  const panelBg = styles.sidebar_bg_colour || "#202124";
  const connectedCount = connectedUsers.length;
  const assignedCount = tokens.filter((t) => toInt(t.controlled_user_id)).length;

  return (
    <div
      style={{
        margin: isMobile ? "8px 10px 14px 10px" : "8px 12px 14px 12px",
        borderRadius: 10,
        background: "#1f2027",
        color: fontColour,
        fontFamily: mainFont,
        flex: "0 0 auto",
      }}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          color: "#fff",
          border: "none",
          padding: "10px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 800,
          fontSize: 16,
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
              transition: "transform 0.15s",
            }}
          >
            ▸
          </span>
          Connected Players
        </span>
        <span
          style={{
            background: "#282931",
            border: "1px solid #3a3b44",
            borderRadius: 999,
            padding: "2px 10px",
            fontSize: 13,
            fontWeight: 900,
            color: "#ffc73c",
          }}
          title="Connected players"
        >
          {connectedCount}
        </span>
      </button>

      <div
        style={{
          overflow: "hidden",
          maxHeight: collapsed ? 0 : 2000,
          transition: "max-height 220ms ease",
          padding: collapsed ? "0 12px" : "0 12px 12px 12px",
        }}
      >
        <div
          style={{
            background: panelBg,
            borderRadius: 8,
            padding: 10,
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 10 }}>
            Click a connected player and choose which player token they control.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(0,1fr)",
              gap: 10,
              alignItems: "start",
            }}
          >
            <div
              style={{
                minHeight: 0,
                maxHeight: 180,
                overflowY: "auto",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                background: "rgba(0,0,0,0.18)",
              }}
            >
              {connectedUsers.length === 0 ? (
                <div style={{ padding: 10, opacity: 0.7, fontSize: 13 }}>
                  No connected players detected.
                </div>
              ) : (
                connectedUsers.map((viewer) => {
                  const assignedToken = assignmentMap.get(viewer.user_id);
                  const isSelected = viewer.user_id === selectedUserId;

                  return (
                    <button
                      key={`connected-player-${viewer.user_id}`}
                      type="button"
                      onClick={() => setSelectedUserId(viewer.user_id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 3,
                        border: "none",
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                        background: isSelected ? "rgba(255,255,255,0.12)" : "transparent",
                        color: fontColour,
                        padding: "10px 10px",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{viewer.label}</span>
                      <span style={{ fontSize: 12, opacity: 0.75 }}>
                        {assignedToken ? `Token: ${assignedToken.label}` : "No player token assigned"}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div
              style={{
                minHeight: 0,
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                background: "rgba(0,0,0,0.18)",
                padding: 10,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                {selectedUser ? selectedUser.label : "Select a player"}
              </div>

              <select
                value={selectedTokenId}
                onChange={(e) => setSelectedTokenId(e.target.value)}
                disabled={!selectedUser || loadingTokens || busyTokenId != null}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "#111",
                  color: "#fff",
                  padding: "9px 10px",
                  marginBottom: 10,
                  outline: "none",
                }}
              >
                <option value="">
                  {loadingTokens ? "Loading player tokens..." : "Choose a player token"}
                </option>
                {tokens.map((token) => (
                  <option key={`player-token-option-${token.id}`} value={token.id}>
                    {token.label}
                    {token.controlled_user_id
                      ? ` — linked to ${userLabelMap.get(token.controlled_user_id) || token.controlled_username || `user ${token.controlled_user_id}`}`
                      : ""}
                  </option>
                ))}
              </select>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={saveAssignment}
                  disabled={!selectedUser || !selectedToken || busyTokenId != null}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontWeight: 700,
                    cursor: !selectedUser || !selectedToken || busyTokenId != null ? "default" : "pointer",
                    opacity: !selectedUser || !selectedToken || busyTokenId != null ? 0.55 : 1,
                  }}
                >
                  Save Link
                </button>

                <button
                  type="button"
                  onClick={clearAssignment}
                  disabled={!selectedUser || !assignmentMap.get(selectedUser.user_id) || busyTokenId != null}
                  style={{
                    background: "#7f1d1d",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontWeight: 700,
                    cursor:
                      !selectedUser || !assignmentMap.get(selectedUser.user_id) || busyTokenId != null
                        ? "default"
                        : "pointer",
                    opacity:
                      !selectedUser || !assignmentMap.get(selectedUser.user_id) || busyTokenId != null
                        ? 0.55
                        : 1,
                  }}
                >
                  Clear Link
                </button>
              </div>

              <div style={{ fontSize: 12, opacity: 0.72, marginTop: 10, minHeight: 18 }}>
                {status ||
                  (selectedUser && assignmentMap.get(selectedUser.user_id)
                    ? `Current token: ${assignmentMap.get(selectedUser.user_id)?.label || "Assigned"}`
                    : "No token linked for this player.")}
              </div>
            </div>
          </div>

          <div style={{ height: 10 }} />

          <button
            onClick={() => setTokensCollapsed((c) => !c)}
            aria-expanded={!tokensCollapsed}
            style={{
              width: "100%",
              textAlign: "left",
              background: "rgba(0,0,0,0.18)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10,
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 800,
              fontSize: 14,
              justifyContent: "space-between",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  display: "inline-block",
                  transform: tokensCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                  transition: "transform 0.15s",
                }}
              >
                ▸
              </span>
              Player Token Assignments
            </span>
            <span
              style={{
                background: "#282931",
                border: "1px solid #3a3b44",
                borderRadius: 999,
                padding: "2px 10px",
                fontSize: 12,
                fontWeight: 900,
                color: "#ffc73c",
              }}
              title="Assigned player tokens"
            >
              {assignedCount}/{tokens.length}
            </span>
          </button>

          <div
            style={{
              overflow: "hidden",
              maxHeight: tokensCollapsed ? 0 : 320,
              transition: "max-height 220ms ease",
            }}
          >
            <div
              style={{
                marginTop: tokensCollapsed ? 0 : 10,
                minHeight: 0,
                maxHeight: 260,
                overflowY: "auto",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                background: "rgba(0,0,0,0.18)",
              }}
            >
              {loadingTokens ? (
                <div style={{ padding: 10, opacity: 0.7, fontSize: 13 }}>
                  Loading player tokens...
                </div>
              ) : tokens.length === 0 ? (
                <div style={{ padding: 10, opacity: 0.7, fontSize: 13 }}>
                  No player tokens found on this map.
                </div>
              ) : (
                tokens.map((token) => {
                  const assignedUserId = toInt(token.controlled_user_id);
                  const assignedUserLabel = assignedUserId
                    ? userLabelMap.get(assignedUserId) ||
                      token.controlled_username ||
                      `User ${assignedUserId}`
                    : null;

                  const isAssignedOnline =
                    assignedUserId && onlineMapUserIds instanceof Set
                      ? onlineMapUserIds.has(assignedUserId)
                      : false;

                  return (
                    <div
                      key={`player-token-assignment-row-${token.id}`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        padding: "10px 10px",
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{token.label}</span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            padding: "2px 7px",
                            borderRadius: 999,
                            background: assignedUserId
                              ? isAssignedOnline
                                ? "rgba(34,197,94,0.16)"
                                : "rgba(245,158,11,0.16)"
                              : "rgba(255,255,255,0.08)",
                            color: assignedUserId
                              ? isAssignedOnline
                                ? "#86efac"
                                : "#fcd34d"
                              : "rgba(255,255,255,0.7)",
                            border: assignedUserId
                              ? isAssignedOnline
                                ? "1px solid rgba(34,197,94,0.3)"
                                : "1px solid rgba(245,158,11,0.3)"
                              : "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          {assignedUserId
                            ? isAssignedOnline
                              ? "Assigned · Online"
                              : "Assigned · Offline"
                            : "Unassigned"}
                        </span>
                      </div>

                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        {assignedUserId
                          ? `Linked to ${assignedUserLabel}`
                          : "Not linked to any player"}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}